/* eslint-disable no-inline-styles/no-inline-styles */
import {
  Box,
  Button,
  Typography,
  useTheme,
  Paper,
  Alert,
  Stack,
} from '@mui/material';
import UploadIcon from '@mui/icons-material/Upload';
import CircularProgress from '@mui/material/CircularProgress';
import { useState, useRef } from 'react';
import fileDownload from 'js-file-download';
import { useAxiosPrivate } from '../../../hooks';

const DatabaseChecker = () => {
  const theme = useTheme();
  const axiosPrivate = useAxiosPrivate();
  const [isUploading, setIsUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      setSelectedFile(event.target.files[0]);
      setUploadSuccess(false);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    setIsUploading(true);
    const formData = new FormData();
    formData.append('file', selectedFile);

    try {
      const response = await axiosPrivate.post(
        '/reports/database-check',
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          responseType: 'blob',
        },
      );

      fileDownload(
        response.data,
        `expo-database-report-${new Date().toISOString()}.json`,
      );
      setUploadSuccess(true);
      setSelectedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      console.error('Upload failed:', error);
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 500, mx: 'auto', p: 3 }}>
      <Paper elevation={2} sx={{ p: 4, borderRadius: 2 }}>
        <Stack spacing={3} alignItems="center">
          <Typography variant="h5" fontWeight="500">
            Upload Expo File
          </Typography>

          {uploadSuccess && (
            <Alert severity="success" sx={{ width: '100%' }}>
              File uploaded successfully!
            </Alert>
          )}

          <Box sx={{ width: '100%' }}>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
              id="file-upload"
            />

            <Stack spacing={2} alignItems="center">
              <Button
                variant="outlined"
                component="label"
                htmlFor="file-upload"
                fullWidth
                sx={{
                  py: 2,
                  border: `2px dashed ${theme.palette.primary.main}`,
                  '&:hover': {
                    border: `2px dashed ${theme.palette.primary.dark}`,
                  },
                }}
              >
                {selectedFile ? selectedFile.name : 'Choose Excel File'}
              </Button>

              <Button
                variant="contained"
                startIcon={
                  isUploading ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <UploadIcon />
                  )
                }
                onClick={handleUpload}
                disabled={isUploading || !selectedFile}
                fullWidth
                sx={{
                  py: 1.5,
                  backgroundColor: theme.palette.primary.main,
                  '&:hover': {
                    backgroundColor: theme.palette.primary.dark,
                  },
                }}
              >
                {isUploading ? 'Uploading...' : 'Upload'}
              </Button>
            </Stack>
          </Box>

          <Typography variant="caption" color="text.secondary">
            Supported formats: .xlsx, .xls
          </Typography>
        </Stack>
      </Paper>
    </Box>
  );
};

export default DatabaseChecker;
